import React, { useState, useEffect } from "react";
import { Input, Button, Modal, ModalHeader, ModalFooter, ModalBody, Label, FormText, FormGroup } from "reactstrap";
import axios from "axios";
import { getCookie } from "../jwt/_helpers/cookie";
import Countries from "../utils/countriesExtended.json";
import Select from "react-select";
import LoaderInner from "./LoaderInner";
import { Trans, useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const SubscriptionConfirmModal = ({
  openModal,
  setToggleModal,
  currentUser,
  priceID,
  setError,
}) => {
  const tokenAPI = getCookie("token");
  const { t } = useTranslation();

  const options = {
    method: "POST",
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${tokenAPI}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json"
    }
  };

  const [inProgress, setInProgress] = useState(false);
  const [isEditing, setIsEditing] = useState({
    firstname: false,
    lastname: false,
  });
  const [country, setCountry] = useState(null);
  const [firstName, setFirstName] = useState(currentUser? currentUser.first_name: "");
  const [lastName, setLastName] = useState(currentUser? currentUser.last_name: "");
  
  const [consentDocument, setConsentDocument] = useState(null);
  const [agreedRefundPolicy, setAgreedRefundPolicy] = useState(true);

  const fetchLatestConsentRefundDocument = async () => {
    try {
      const response = await axios.get(`${API_URL}consent-refund-documents/`, options);
      let documents = response.data.results;
      if(documents.length>0) {
        setConsentDocument(documents[0]);
      }
    } catch (error) {
      console.error('Error fetching latest consent document:', error);
    }
  };

  useEffect(() => {
    fetchLatestConsentRefundDocument();
  }, []);

  const toggleEdit = (field) => {
    setIsEditing({ ...isEditing, [field]: !isEditing[field] });
  };

  const subscriptionData = {
    "firstname": firstName,
    "lastname": lastName,
    "price_id": priceID,
    "country": country? country.value.toUpperCase(): "HR",
  };

  const subscribe = () => {
    setInProgress(true);
    const subscriptionOptions = {
      method: "POST",
      data: subscriptionData,
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${tokenAPI}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json"
      }
    };

    axios(`${API_URL}users/record-refund-consent/`, options)
    .then(res => {
      axios(`${API_URL}create-subscription-checkout-session/`, subscriptionOptions)
      .then(res => {
        setToggleModal(false);
        setInProgress(false);
        let checkoutUrl = res.data;
        window.location.assign(checkoutUrl);
      })
      .catch(err => {
        setInProgress(false);
        setToggleModal(false);
        setError(t("subscription.confirmModal.body.message.error"));
      });
    })
    .catch(err => {
      setInProgress(false);
      setToggleModal(false);
      setError(t("subscription.confirmModal.body.message.error"));
    });
  };

  return (
    <Modal isOpen={openModal} centered={true}>
      <ModalHeader>{t("subscription.confirmModal.header")}</ModalHeader>
      <ModalBody>
          <LoaderInner show={inProgress} />

          <div>
            <Label style={{ fontWeight: 'bold' }}>{t("subscription.confirmModal.body.form.label.firstName")}</Label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Input
                value={firstName}
                disabled={!isEditing.firstname}
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
              />
              <i className="btn btn-ml fas fa-edit" onClick={() => toggleEdit('firstname')} style={{ marginLeft: '10px' }} />
            </div>
            <FormText color="muted">{t("subscription.confirmModal.body.form.help_text.firstName")}</FormText>
          </div>
  
          <div className="mt-3">
            <Label style={{ fontWeight: 'bold' }}>{t("subscription.confirmModal.body.form.label.lastName")}</Label>
            <div style={{ display: 'flex', alignItems: 'center'}}>
              <Input
                value={lastName}
                disabled={!isEditing.lastname}
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
              />
              <i className="btn btn-ml fas fa-edit" onClick={() => toggleEdit('lastname')} style={{ marginLeft: '10px' }} />
            </div>
            <FormText color="muted">{t("subscription.confirmModal.body.form.help_text.lastName")}</FormText>
          </div>

          <div className="mt-3 mb-3">
              <FormGroup>
                <Label style={{ fontWeight: 'bold' }}>{t("subscription.confirmModal.body.form.label.country")}</Label>
                <Select
                  components={{ IndicatorSeparator:() => null }}
                  placeholder={t("general.select.placeholder")}
                  name="country"
                  options={Countries}
                  value={country}
                  onChange={e => setCountry(e)}
                />
              </FormGroup>
              <FormText color="muted">{t("subscription.confirmModal.body.form.help_text.country")}</FormText>
          </div>

          <div className="mt-3 mb-3">
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  className="form-check-input mt-0"
                  name="acceptRefundPolicy"
                  checked={agreedRefundPolicy}
                  style={{ width: '14px', height: '14px', transform: 'scale(1.2)' }}
                  onChange={
                    () => setAgreedRefundPolicy(!agreedRefundPolicy)
                  }
                />{' '}
                <div className="mt-1">
                  <Trans i18nKey="subscription.confirmModal.body.form.label.refundPolicyAgreement">
                    By proceeding, I acknowledge and accept that this payment is final and non-refundable. <a href={consentDocument? consentDocument.refund_policy_link: ""} style={{fontWeight: 700}} target="_blank" rel="noopener noreferrer">Refund Policy</a>
                  </Trans>
                </div>
              </Label>
            </FormGroup>
          </div>

        <b>{t("subscription.confirmModal.body.form.message.note")}</b>

      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setToggleModal(false)}>
          {t("subscription.confirmModal.button.cancel")}
        </Button>
        <Button color="success" onClick={() => subscribe()} disabled={inProgress || country === null || !agreedRefundPolicy}>
          {t("subscription.confirmModal.button.confirm")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SubscriptionConfirmModal;